import * as Api from '@ViewModels';
import { ResourceAutoCompleteViewModelType } from '@ViewModels';
import {
	ISimpleAutoCompleteSearchFieldItemSelectionEvent,
	SimpleAutoCompleteSearchField,
} from '@WebComponents/autocomplete/SimpleAutoCompleteSearchField';
import { LoadingSpinner } from '@WebComponents/LoadingSpinner';
import { IModalProps, Modal } from '@WebComponents/Modal';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../models/Logging';
import { useContactAssignOwnerMutation } from '../../../../queries';
import { useVisibilityJob } from '../../../containers/PeopleV2/PeopleContainer/PeopleActionsBar/useActionBarStatus';
import { Button } from '../../Button';
import { styleSheet } from './styles';

export interface IAssignContactOwnerProps extends Omit<IModalProps, 'onRequestClose'> {
	selectedContactIds: string[];
	excludedContactIds?: string[];
	filterCriteria?: Api.IContactsFilterRequest;
	ownershipFilter?: Api.IOwnershipFilter;
	includeFilterInRequest?: boolean;
	onSuccess?: () => void;
	onCancel?: () => void;
}

export const AssignContactOwner: React.FC<IAssignContactOwnerProps> = ({
	className = '',
	isOpen,
	selectedContactIds,
	excludedContactIds = [],
	filterCriteria,
	ownershipFilter,
	includeFilterInRequest = false,
	onSuccess,
	onCancel,
	...restProps
}) => {
	// State
	const [selectedUser, setSelectedUser] = React.useState<Api.IUser | null>(null);

	// Hooks
	const { logInput, logApiError } = useEventLogging();
	const errorMessages = useErrorMessages();

	// Close handlers
	const handleClose = React.useCallback(() => {
		if (onCancel) {
			onCancel();
		}
	}, [onCancel]);

	// Job monitoring
	const { startWatchingJob, isJobRunning } = useVisibilityJob(() => {
		toast.success('Contact owner assigned successfully!');
		if (onSuccess) {
			onSuccess();
		}
	});

	// React Query mutation
	const contactAssignOwnerMutation = useContactAssignOwnerMutation({
		onSuccess: systemJob => {
			startWatchingJob(systemJob);
		},
		onError: error => {
			logApiError('BulkAssign-Error', error);
			errorMessages.pushApiError(error);
		},
	});

	const handleUserSelected = (event: ISimpleAutoCompleteSearchFieldItemSelectionEvent) => {
		setSelectedUser(event.selection as Api.IUser);
	};

	const handleAssignOwner = () => {
		if (!selectedUser) return;

		// filter and includeContactIds are mutually exclusive:
		// - When using filter mode (includeFilterInRequest=true): send filter + excludeContactIds
		// - When selecting specific contacts: send includeContactIds only
		const request = {
			excludeContactIds: includeFilterInRequest ? excludedContactIds : undefined,
			filter: includeFilterInRequest ? filterCriteria : undefined,
			ownershipFilter: includeFilterInRequest ? ownershipFilter : undefined,
			includeContactIds: includeFilterInRequest ? undefined : selectedContactIds,
			newOwnerId: selectedUser.id,
		};

		logInput('Done', 'Click', {
			excludeContactIds: request.excludeContactIds?.length ?? 0,
			filter: request.filter,
			includeContactIds: request.includeContactIds?.length ?? 0,
			newOwnerId: selectedUser.id,
		});

		contactAssignOwnerMutation.mutate(request);
	};

	return (
		<Modal {...restProps} className={className} isOpen={isOpen} onRequestClose={handleClose} useDefaultHeader={false}>
			<h2 className={css(styleSheet.header)}>Assign Contact Owner</h2>
			<>
				{isJobRunning ? (
					<LoadingSpinner type='large' />
				) : (
					<div className={css(styleSheet.content)}>
						<SimpleAutoCompleteSearchField
							disabled={contactAssignOwnerMutation.isPending || isJobRunning}
							onClear={() => setSelectedUser(null)}
							onItemSelected={handleUserSelected}
							type={ResourceAutoCompleteViewModelType.User}
							placeholder='Search co-workers or add yourself'
							pageSize={5}
							resultsLimit={5}
						/>

						<div className={css(styleSheet.footer)}>
							<Button
								kind='primary'
								onClick={handleAssignOwner}
								disabled={!selectedUser || contactAssignOwnerMutation.isPending}
							>
								Save
							</Button>
							<Button kind='reverse' onClick={handleClose} disabled={contactAssignOwnerMutation.isPending}>
								Cancel
							</Button>
						</div>
					</div>
				)}
			</>
		</Modal>
	);
};
